import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { CountryCombobox } from '@/components/country-combobox'
import { notifyError } from '@/components/notify'
import { TimezoneCombobox } from '@/components/timezone-combobox'
import { Button } from '@/components/ui/button'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMe } from '@/features/auth/api'
import { applyServerErrors } from '@/lib/form-errors'
import { browserTimeZone } from '@/lib/timezones'
import { emptyToNull } from '@/lib/utils'
import type { Location } from '@/types/api'
import { useCreateLocation, useUpdateLocation, type LocationInput } from '../api'

const optionalNumber = (min: number, max: number, label: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= min && Number(v) <= max), `${label} must be between ${min} and ${max}`)

export const locationSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  address: z.string().max(255),
  city: z.string().max(120),
  postal_code: z.string().max(20),
  country: z.string().length(2).nullable(),
  timezone: z.string().min(1, 'Choose a timezone'),
  latitude: optionalNumber(-90, 90, 'Latitude'),
  longitude: optionalNumber(-180, 180, 'Longitude'),
  description: z.string().max(2000),
  contact_name: z.string().max(120),
  contact_email: z.union([z.literal(''), z.email('Enter a valid email')]),
  contact_phone: z.string().max(40),
})
export type LocationFormValues = z.infer<typeof locationSchema>

export const LOCATION_FIELDS = Object.keys(locationSchema.shape)

export function toFormValues(location: Location | undefined, defaultTz: string): LocationFormValues {
  return {
    name: location?.name ?? '',
    address: location?.address ?? '',
    city: location?.city ?? '',
    postal_code: location?.postal_code ?? '',
    country: location?.country ?? null,
    timezone: location?.timezone ?? defaultTz,
    latitude: location?.latitude?.toString() ?? '',
    longitude: location?.longitude?.toString() ?? '',
    description: location?.description ?? '',
    contact_name: location?.contact_name ?? '',
    contact_email: location?.contact_email ?? '',
    contact_phone: location?.contact_phone ?? '',
  }
}

export function toPayload(values: LocationFormValues): LocationInput {
  return {
    name: values.name.trim(),
    address: emptyToNull(values.address),
    city: emptyToNull(values.city),
    postal_code: emptyToNull(values.postal_code),
    country: values.country,
    timezone: values.timezone,
    latitude: values.latitude.trim() === '' ? null : Number(values.latitude),
    longitude: values.longitude.trim() === '' ? null : Number(values.longitude),
    description: emptyToNull(values.description),
    contact_name: emptyToNull(values.contact_name),
    contact_email: emptyToNull(values.contact_email),
    contact_phone: emptyToNull(values.contact_phone),
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-4">
      <legend className="mb-3 text-[11.5px] font-semibold tracking-wider text-muted-foreground uppercase">{title}</legend>
      {children}
    </fieldset>
  )
}

export function LocationForm({
  location,
  onSuccess,
  onCancel,
  submitLabel,
  compact = false,
  formId,
  hideActions = false,
  onPendingChange,
}: {
  location?: Location
  onSuccess: (location: Location) => void
  onCancel?: () => void
  submitLabel?: string
  /** Onboarding variant: only the essentials. */
  compact?: boolean
  formId?: string
  hideActions?: boolean
  onPendingChange?: (pending: boolean) => void
}) {
  const { data: me } = useMe()
  const create = useCreateLocation()
  const update = useUpdateLocation(location?.id ?? '')
  const form = useForm<LocationFormValues>({
    resolver: zodResolver(locationSchema),
    defaultValues: toFormValues(location, me?.current_tenant?.timezone ?? browserTimeZone()),
  })
  const { errors, isSubmitting } = form.formState

  const onSubmit = form.handleSubmit(async (values) => {
    onPendingChange?.(true)
    try {
      const payload = toPayload(values)
      const saved = location ? await update.mutateAsync(payload) : await create.mutateAsync(payload)
      onSuccess(saved)
    } catch (error) {
      if (!applyServerErrors(error, form.setError, { fields: LOCATION_FIELDS })) notifyError(error)
    } finally {
      onPendingChange?.(false)
    }
  })

  return (
    <form id={formId} onSubmit={onSubmit} className="grid gap-7" noValidate>
      <FormError message={errors.root?.server?.message} />
      <Section title="Basics">
        <Field label="Name" required error={errors.name?.message}>
          <Input placeholder="Downtown flagship store" autoFocus {...form.register('name')} />
        </Field>
        <Field label="Timezone" error={errors.timezone?.message} hint="Schedules for displays here run on this local time.">
          <Controller control={form.control} name="timezone" render={({ field }) => <TimezoneCombobox value={field.value} onChange={field.onChange} />} />
        </Field>
      </Section>

      <Section title="Address">
        <Field label="Street address" optional error={errors.address?.message}>
          <Input autoComplete="street-address" {...form.register('address')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="City" optional error={errors.city?.message}>
            <Input autoComplete="address-level2" {...form.register('city')} />
          </Field>
          <Field label="Postal code" optional error={errors.postal_code?.message}>
            <Input autoComplete="postal-code" {...form.register('postal_code')} />
          </Field>
        </div>
        <Field label="Country" optional error={errors.country?.message}>
          <Controller control={form.control} name="country" render={({ field }) => <CountryCombobox value={field.value} onChange={field.onChange} />} />
        </Field>
        {!compact && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Latitude" optional error={errors.latitude?.message}>
              <Input inputMode="decimal" placeholder="44.8125" {...form.register('latitude')} />
            </Field>
            <Field label="Longitude" optional error={errors.longitude?.message}>
              <Input inputMode="decimal" placeholder="20.4612" {...form.register('longitude')} />
            </Field>
          </div>
        )}
      </Section>

      {!compact && (
        <>
          <Section title="Contact">
            <Field label="Contact name" optional error={errors.contact_name?.message}>
              <Input autoComplete="name" {...form.register('contact_name')} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" optional error={errors.contact_email?.message}>
                <Input type="email" autoComplete="email" {...form.register('contact_email')} />
              </Field>
              <Field label="Phone" optional error={errors.contact_phone?.message}>
                <Input type="tel" autoComplete="tel" {...form.register('contact_phone')} />
              </Field>
            </div>
          </Section>
          <Section title="Notes">
            <Field label="Description" optional error={errors.description?.message}>
              <Textarea rows={3} placeholder="Opening hours, access instructions, anything installers should know…" {...form.register('description')} />
            </Field>
          </Section>
        </>
      )}

      {!hideActions && (
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={isSubmitting}>
            {submitLabel ?? (location ? 'Save changes' : 'Create location')}
          </Button>
        </div>
      )}
    </form>
  )
}
